// CSV building + browser download for the trial-points report pages.

import moment from 'moment-timezone';

const escapeCell = (value) => {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const buildCsv = (headers, rows) =>
  [headers, ...rows].map((row) => row.map(escapeCell).join(',')).join('\n');

export const downloadCsv = (filename, headers, rows) => {
  const blob = new Blob([buildCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

// Ledger convention: 'consumed' rows are stored negative and 'refunded' positive, so a raw
// points_consumed reads as -5. The teacher-wise CSV's Deducted column is a positive magnitude, so
// the session rows are flipped to match — otherwise per-teacher sums across the two files disagree
// by sign and cannot be reconciled.
const asMagnitude = (points) => (typeof points === 'number' ? Math.abs(points) : 0);

const CONVERSION_TYPE_LABELS = { conversion_1on1: '1on1', conversion_gc: 'GC' };

export const SESSION_CSV_HEADERS = [
  'Teacher UUID',
  'Teacher ID',
  'Teacher Name',
  'Email',
  'Session UUID',
  'Session Date',
  'Status',
  'Duration (mins)',
  'Student UUID',
  'Student Name',
  'Lead Score',
  'Deducted',
  'Refunded',
  'Conversion Points',
  'Conversion Type'
];

// Session dates render in the CMS user's selected timezone, matching the on-screen tables. No
// timezone (the store value can be empty on first paint) falls back to local rather than throwing.
const formatSessionDate = (sessionStart, ianaTimezone) => {
  if (!sessionStart) return '';
  const m = moment(sessionStart);
  return (ianaTimezone ? m.tz(ianaTimezone) : m).format('YYYY-MM-DD HH:mm z');
};

// buildSessionCsvRows maps the /trial_points/sessions_export rows to SESSION_CSV_HEADERS order.
// Pure, so the column contract is unit-tested rather than eyeballed in a downloaded file.
export const buildSessionCsvRows = (rows, ianaTimezone) =>
  (rows || []).map((row) => [
    row.teacher_uuid || '',
    row.teacher_id ?? '',
    row.teacher_name || '',
    row.email || '',
    row.session_uuid || '',
    formatSessionDate(row.session_start, ianaTimezone),
    row.status_text || row.status || '',
    row.duration_minutes ?? '',
    row.student_uuid || '',
    row.student_name || '',
    // A missing lead score is genuinely unknown; a zero points figure is a real value, so the two
    // must not collapse to the same blank cell.
    row.lead_score ?? '',
    asMagnitude(row.points_consumed),
    asMagnitude(row.points_refunded),
    row.conversion_points ?? 0,
    CONVERSION_TYPE_LABELS[row.conversion_type] || ''
  ]);
